/* global window, document */
// Browser-side presentation export. The local app may use production Functions,
// so an undeployed export action cannot be the only way to download a report.
// Input is the same resolved, number-preserving block payload as PDF export.
const advisorPptxColors = { purple: '3A1163', accent: '6D28B5', lilac: 'F3EBFC', ink: '231B32',
  muted: '685C78', white: 'FFFFFF', line: 'E5DDEF', green: '198274', red: 'B74753' };
let advisorPptxLoading = null;

const loadAdvisorPptx = () => {
  if (window.PptxGenJS) return Promise.resolve(window.PptxGenJS);
  if (advisorPptxLoading) return advisorPptxLoading;
  advisorPptxLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/vendor/pptxgen.bundle.js?v=4.0.1';
    script.async = true;
    script.onload = () => window.PptxGenJS ? resolve(window.PptxGenJS) : reject(new Error('PowerPoint library unavailable'));
    script.onerror = () => reject(new Error('PowerPoint library unavailable'));
    document.head.appendChild(script);
  }).catch((error) => { advisorPptxLoading = null; throw error; });
  return advisorPptxLoading;
};

const advisorPptxText = (value) => String(value ?? '').replace(/[\u0000-\u001f]/g, ' ').trim();

const buildAdvisorPptx = async ({ title, periodLabel, year, sections, labels = {}, presentation, quarter, periodId }) => {
  const PptxGenJS = await loadAdvisorPptx();
  const periodSuffix = /^\d{4}-\d{2}$/.test(String(periodId || '')) ? '-' + periodId : quarter ? '-Q' + quarter : '';
  if (presentation) return window.advisorPresentationPptx(PptxGenJS, presentation, `radgiverrapport-${year}${periodSuffix}.pptx`);
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Plumme';
  pptx.title = title;
  pptx.lang = PlummeI18n.intlLocale();
  const shape = pptx.ShapeType;
  const C = advisorPptxColors;
  let page = 0;
  const addText = (slide, value, x, y, w, h, options = {}) => slide.addText(advisorPptxText(value), {
    x, y, w, h, fontFace: 'Aptos', fontSize: 14, color: C.ink, margin: 0,
    breakLine: false, valign: 'mid', fit: 'shrink', ...options,
  });
  const box = (slide, x, y, w, h, color) => slide.addShape(shape.rect, {
    x, y, w, h, line: { color, transparency: 100 }, fill: { color },
  });
  const slideBase = (heading, number, continued = false) => {
    const slide = pptx.addSlide();
    slide.background = { color: C.white };
    box(slide, 0, 0, 13.333, 0.12, C.accent);
    addText(slide, labels.brand || 'PLUMME', 0.62, 0.35, 5, 0.25,
      { fontSize: 10, bold: true, color: C.accent, charSpacing: 1.1 });
    addText(slide, `${String(number).padStart(2, '0')}  ·  ${heading}${continued ? ` (${labels.continued || 'continued'})` : ''}`,
      0.62, 0.83, 12.05, 0.65, { fontSize: 27, bold: true });
    box(slide, 0.62, 1.58, 12.05, 0.02, C.line);
    addText(slide, periodLabel || String(year), 0.62, 7.12, 9, 0.2, { fontSize: 9, color: C.muted });
    addText(slide, String(++page).padStart(2, '0'), 12.03, 7.1, 0.64, 0.2,
      { fontSize: 9, color: C.muted, align: 'right' });
    return slide;
  };

  const cover = pptx.addSlide();
  cover.background = { color: C.purple };
  box(cover, 0, 0, 0.14, 7.5, C.accent);
  addText(cover, labels.brand || 'PLUMME', 0.75, 0.68, 6, 0.3,
    { fontSize: 14, color: C.white, bold: true, charSpacing: 1.6 });
  box(cover, 0.75, 1.35, 1.1, 0.06, 'C9AEEC');
  addText(cover, title, 0.75, 2.05, 11.75, 1.5,
    { fontSize: title.length > 65 ? 34 : 44, bold: true, color: C.white, valign: 'top' });
  addText(cover, periodLabel || String(year), 0.77, 4.14, 11, 0.44,
    { fontSize: 20, color: 'E2D5F1' });
  addText(cover, labels.coverFoot || 'Plumme', 0.77, 6.78, 9, 0.22,
    { fontSize: 11, color: 'CBB7E5' });
  page++;

  for (let start = 0; start < sections.length; start += 14) {
    const slide = slideBase(labels.contents || 'Contents', 0, start > 0);
    sections.slice(start, start + 14).forEach((section, local) => {
      const x = 0.75 + (local >= 7 ? 6.2 : 0);
      const y = 1.95 + (local % 7) * 0.63;
      addText(slide, String(start + local + 1).padStart(2, '0'), x, y, 0.48, 0.36,
        { fontSize: 12, bold: true, color: C.accent });
      addText(slide, section.title, x + 0.56, y, 5.4, 0.36, { fontSize: 14, bold: true });
    });
  }

  const addChart = (slide, block, y, x = 0.84, w = 11.7, h = 3.72) => {
    if (block.variant === 'waterfall') {
      const opening = Number(block.opening) || 0;
      const closing = Number(block.closing) || 0;
      const bars = [{ from: 0, to: opening, value: opening, label: block.openingLabel, kind: 'total' }];
      let running = opening;
      (block.series[0]?.values || []).forEach((rawValue, index) => {
        const value = Number(rawValue) || 0;
        const from = running;
        running += value;
        bars.push({ from, to: running, value, label: block.categories[index], kind: value < 0 ? 'down' : 'up' });
      });
      bars.push({ from: 0, to: closing, value: closing, label: block.closingLabel, kind: 'total' });
      const levels = bars.flatMap((bar) => [bar.from, bar.to, 0]);
      const max = Math.max(1, ...levels);
      const min = Math.min(0, ...levels);
      const plotTop = y + 0.45;
      const plotH = h - 1.35;
      const at = (value) => plotTop + (max - value) / (max - min || 1) * plotH;
      const step = w / bars.length;
      const barW = Math.min(0.55, step * 0.58);
      addText(slide, labels.bridgeGuide || 'Green increases the result; red decreases it.',
        x, y, w, 0.25, { fontSize: 10, color: C.muted });
      slide.addShape(shape.line, { x, y: at(0), w, h: 0, line: { color: C.muted, width: 1.2 } });
      bars.forEach((bar, index) => {
        const centerX = x + step * (index + 0.5);
        const top = Math.min(at(bar.from), at(bar.to));
        const barH = Math.max(0.03, Math.abs(at(bar.from) - at(bar.to)));
        box(slide, centerX - barW / 2, top, barW, barH,
          bar.kind === 'total' ? C.accent : bar.kind === 'down' ? C.red : C.green);
        if (index < bars.length - 1) slide.addShape(shape.line, {
          x: centerX + barW / 2, y: at(bar.to), w: step - barW, h: 0,
          line: { color: C.muted, width: 0.8, dashType: 'dash' },
        });
        const amount = `${bar.kind === 'total' || bar.value < 0 ? '' : '+'}${Math.round(bar.value).toLocaleString(PlummeI18n.intlLocale())}`;
        addText(slide, amount, centerX - step / 2 + 0.02, y + h - 0.76, step - 0.04, 0.24,
          { fontSize: 10, bold: true, color: bar.kind === 'down' ? C.red : bar.kind === 'up' ? C.green : C.accent,
            align: 'center' });
        addText(slide, bar.label, centerX - step / 2 + 0.02, y + h - 0.48, step - 0.04, 0.43,
          { fontSize: 8, color: C.muted, align: 'center', valign: 'top' });
      });
    } else if (block.colorBySign && block.variant === 'bars') {
      const values = (block.series[0]?.values || []).slice(0, 18).map((value) => Number(value) || 0);
      const categories = (block.categories || []).slice(0, values.length);
      const maximum = Math.max(1, ...values);
      const minimum = Math.min(0, ...values);
      const plotTop = y + 0.2;
      const plotH = h - 0.78;
      const at = (value) => plotTop + (maximum - value) / (maximum - minimum || 1) * plotH;
      const step = w / Math.max(1, values.length);
      const barW = Math.min(0.5, step * 0.57);
      slide.addShape(shape.line, { x, y: at(0), w, h: 0, line: { color: C.muted, width: 1 } });
      values.forEach((value, index) => {
        const centerX = x + step * (index + 0.5);
        if (value) box(slide, centerX - barW / 2, Math.min(at(value), at(0)), barW,
          Math.max(0.03, Math.abs(at(value) - at(0))), value < 0 ? C.red : C.green);
        addText(slide, String(categories[index] || '').slice(0, 3), centerX - step / 2, y + h - 0.48,
          step, 0.23, { fontSize: 9, color: C.muted, align: 'center' });
      });
    } else {
      const cats = (block.categories || []).slice(0, 18);
      const data = (block.series || []).map((series) => ({ name: series.label,
        labels: cats, values: series.values.slice(0, cats.length) }));
      slide.addChart(block.variant === 'line' ? pptx.ChartType.line : pptx.ChartType.bar, data, {
        x, y, w, h, barDir: 'col', chartColors: [C.accent, C.green, C.red],
        showLegend: data.length > 1, legendFontSize: 10, catAxisLabelFontSize: 10,
        valAxisLabelFontSize: 10, showMarker: block.variant === 'line', showBorder: false,
      });
    }
    if (block.marker) addText(slide, `${block.marker.label}: ${block.marker.value}`,
      x, y + h + 0.05, w, 0.25, { fontSize: 10, color: C.muted });
  };

  const linesFor = (block) => {
    if (block.kind === 'text') return [block.text];
    if (block.kind === 'list') return block.items.map((item) => `• ${item}`);
    if (block.kind === 'rows') return block.rows.map((row) => [row.label, row.value, row.note].filter(Boolean).join('  ·  '));
    return [];
  };
  const keyRows = sections.find((section) => /hovedtall|key figures/i.test(section.title))
    ?.blocks.find((block) => block.kind === 'rows')?.rows.slice(0, 4) || [];
  const addTiles = (slide, rows, y, x = 0.77, width = 11.8) => {
    const cols = Math.min(4, rows.length);
    const tileW = (width - (cols - 1) * 0.15) / cols;
    rows.forEach((row, index) => {
      const col = index % cols;
      const rowY = y + Math.floor(index / cols) * 1.27;
      const tileX = x + col * (tileW + 0.15);
      box(slide, tileX, rowY, tileW, 1.12, C.lilac);
      addText(slide, row.label, tileX + 0.14, rowY + 0.13, tileW - 0.28, 0.32,
        { fontSize: 10, color: C.muted, bold: true });
      addText(slide, row.value, tileX + 0.14, rowY + 0.52, tileW - 0.28, 0.42,
        { fontSize: 20, bold: true });
    });
    return Math.ceil(rows.length / cols) * 1.27;
  };
  const addNamedBars = (slide, rows, y) => {
    const scale = Math.max(1, ...rows.map((row) => Math.abs(Number(row.rawValue) || 0)));
    rows.forEach((row, index) => {
      const top = y + index * 0.74;
      addText(slide, row.label, 0.86, top, 8.4, 0.25, { fontSize: 13, bold: true });
      addText(slide, row.value, 10.1, top, 2.35, 0.25, { fontSize: 13, bold: true, align: 'right' });
      box(slide, 0.86, top + 0.34, 11.6, 0.16, C.lilac);
      box(slide, 0.86, top + 0.34, 11.6 * Math.min(1, Math.abs(Number(row.rawValue) || 0) / scale),
        0.16, index === 0 ? C.accent : 'AC8CE3');
    });
    return rows.length * 0.74;
  };
  sections.forEach((section, sectionIndex) => {
    let slide = slideBase(section.title, sectionIndex + 1);
    let y = 1.88;
    const next = () => { slide = slideBase(section.title, sectionIndex + 1, true); y = 1.88; };
    if (/sammendrag|summary/i.test(section.title) && keyRows.length) {
      y += addTiles(slide, keyRows, y) + 0.09;
    }
    (section.blocks || []).forEach((block) => {
      if (block.kind === 'rows' && (block.sourceType === 'kpis' || block.sourceType === 'metrics') && block.rows.length) {
        if (y + Math.ceil(block.rows.length / 4) * 1.27 > 6.7) next();
        y += addTiles(slide, block.rows, y) + 0.13;
        return;
      }
      if (block.kind === 'rows' && block.sourceType === 'namedList' && block.rows.length) {
        const rows = block.rows.slice(0, 6);
        if (y + rows.length * 0.74 > 6.65) next();
        y += addNamedBars(slide, rows, y) + 0.12;
        block.rows.slice(6).forEach((row) => {
          if (y > 6.3) next();
          addText(slide, `${row.label}  ·  ${row.value}`, 0.9, y, 11.5, 0.4, { fontSize: 13 });
          y += 0.46;
        });
        return;
      }
      if (block.kind === 'rows' && block.sourceType === 'risks' && block.rows.length) {
        block.rows.forEach((row) => {
          const cardH = Math.max(1.04, 0.78 + Math.ceil(advisorPptxText(row.note).length / 105) * 0.3);
          if (y + cardH + 0.11 > 6.68) next();
          box(slide, 0.77, y, 11.8, cardH, row.severity === 'critical' ? 'FFF0F2' : C.lilac);
          addText(slide, row.label, 0.97, y + 0.11, 8.9, 0.3, { fontSize: 15, bold: true });
          addText(slide, row.value, 10.3, y + 0.12, 2.02, 0.24,
            { fontSize: 11, bold: true, color: row.severity === 'critical' ? C.red : C.accent, align: 'right' });
          addText(slide, row.note, 0.97, y + 0.48, 11.2, cardH - 0.59,
            { fontSize: 12, color: C.muted, valign: 'top' });
          y += cardH + 0.11;
        });
        return;
      }
      if (section.id === 'utvikling' && block.kind === 'table' && block.values?.length) {
        const rows = block.rows.map((cells, index) => ({ label: cells[0], value: cells[1], rawValue: block.values[index]?.[1] }))
          .filter((row) => Number(row.rawValue) > 0).slice(0, 5);
        if (rows.length) {
          if (y + rows.length * 0.74 > 6.65) next();
          y += addNamedBars(slide, rows, y) + 0.1;
        }
      }
      if (block.kind === 'chart') {
        if (y > 2.45) next();
        addChart(slide, block, y);
        y += 4.2;
        return;
      }
      if (block.kind === 'table') {
        let rowIndex = 0;
        const cols = block.cols.slice(0, 8);
        while (rowIndex < block.rows.length) {
          if (y > 5.9) next();
          const count = Math.max(1, Math.floor((6.65 - y - 0.42) / 0.42));
          const rows = block.rows.slice(rowIndex, rowIndex + count);
          const widths = cols.map((_, i) => i === 0 ? (cols.length === 1 ? 11.4 : 4) : 7.4 / (cols.length - 1));
          const row = (values, header, stripe, tones = []) => {
            if (header || stripe) box(slide, 0.76, y, 11.82, 0.4, header ? C.lilac : 'FAF7FD');
            let x = 0.91;
            values.forEach((value, i) => {
              const tone = tones[i];
              addText(slide, value, x, y + 0.04, widths[i] - 0.14, 0.3,
                { fontSize: header ? 10 : 11, bold: header || i === 0 || Boolean(tone),
                  color: header || tone === 'total' || tone === 'idle' ? C.accent : tone === 'success' ? C.green : tone === 'danger' ? C.red : C.ink,
                  align: i ? 'right' : 'left' });
              x += widths[i];
            });
            y += 0.42;
          };
          row(cols, true, false);
          rows.forEach((values, i) => row(values, false, i % 2 === 1, block.tones?.[rowIndex + i]));
          rowIndex += rows.length;
          y += 0.15;
        }
        return;
      }
      linesFor(block).forEach((line) => {
        const words = advisorPptxText(line).split(/\s+/);
        const chunks = [];
        let current = '';
        words.forEach((word) => {
          if ((current + ' ' + word).length > 112 && current) { chunks.push(current); current = word; }
          else current += (current ? ' ' : '') + word;
        });
        if (current) chunks.push(current);
        while (chunks.length) {
          if (y > 6.25) next();
          const count = Math.max(1, Math.floor((6.65 - y - 0.12) / 0.32));
          const part = chunks.splice(0, count);
          const height = Math.max(0.43, part.length * 0.32 + 0.12);
          if (block.kind === 'rows' && part.length === 1) box(slide, 0.76, y, 11.82, height, 'FAF7FD');
          addText(slide, part.join('\n'), 0.91, y + 0.04, 11.42, height - 0.08,
            { fontSize: block.kind === 'text' ? 15 : 12, valign: 'top' });
          y += height + 0.1;
        }
      });
      y += 0.12;
    });
  });
  const fileName = `radgiverrapport-${Number(year) || new Date().getFullYear()}.pptx`;
  await pptx.writeFile({ fileName });
  return { fileName };
};

Object.assign(window, { buildAdvisorPptx });
